import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);

  const applyMe = (data) => {
    setUser(data);
    setIsPremium(data.is_premium || data.unlimited_access || false);
    setTrialDaysLeft(typeof data.trial_days_left === 'number' ? data.trial_days_left : null);
    setTrialExpired(!!data.trial_expired);
  };

  useEffect(() => {
    const token = localStorage.getItem('intiviv_token');
    if (token) {
      axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => applyMe(r.data))
        .catch(() => localStorage.removeItem('intiviv_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/api/auth/login`, { email, password });
    localStorage.setItem('intiviv_token', r.data.token);
    // Pull fresh /me to get trial info immediately
    try {
      const me = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${r.data.token}` } });
      applyMe(me.data);
    } catch (_) {
      setUser(r.data.user);
    }
    return r.data;
  };

  const register = async (email, name, password, _legacyDob, phone) => {
    const r = await axios.post(`${API}/api/auth/register`, { email, name, password, phone });
    localStorage.setItem('intiviv_token', r.data.token);
    try {
      const me = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${r.data.token}` } });
      applyMe(me.data);
    } catch (_) {
      setUser(r.data.user);
    }
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem('intiviv_token');
    localStorage.removeItem('intiviv_onboarding_done');
    setUser(null);
    setIsPremium(false);
    setTrialDaysLeft(null);
    setTrialExpired(false);
  };

  const authHeader = () => {
    const token = localStorage.getItem('intiviv_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getAge = () => {
    if (!user?.date_of_birth) return null;
    const dob = new Date(user.date_of_birth);
    const age = Math.floor((new Date() - dob) / (365.25 * 24 * 3600 * 1000));
    return age;
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user, loading, isPremium,
      trialDaysLeft, trialExpired, isAdmin,
      login, register, logout, authHeader, getAge,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
